import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  Grid,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  CountResult,
  OrganizationDTO,
  UserDetailsDTO,
} from "@beable/roster-backend-client-v1";
import React, { useEffect, useState } from "react";

import AdminsTable from "./AdminsTable";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import UserDetails from "./UserDetails";
import dataViewService from "../../services/DataViewService";
import { getStyles } from "./styles";

interface AdminsAccordionProps {
  siteId: number;
}

const initialAdminsCountValue: CountResult | null = null;

export default function AdminsAccordion(props: AdminsAccordionProps) {
  const theme = useTheme();
  const styles = getStyles(theme);

  const [isLoadingUserDetails, setIsLoadingUserDetails] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [expanded, setExpanded] = useState<boolean>(false);
  const [adminsCount, setAdminsCount] = useState(initialAdminsCountValue);
  const [selectedAdmin, setSelectedAdmin] = useState<UserDetailsDTO>();
  const [orgs, setOrgs] = useState<OrganizationDTO[]>([]);

  useEffect(() => {
    const fetchAdminsCount = async () => {
      setIsLoading(true);
      const fetchedAdminsCount = await dataViewService.getAdminsCountForSite(
        props.siteId
      );
      setIsLoading(false);

      if (fetchedAdminsCount?.rostered && fetchedAdminsCount.rostered > 0) {
        setAdminsCount(fetchedAdminsCount);
      } else {
        setAdminsCount(initialAdminsCountValue);
      }
    };

    fetchAdminsCount();
  }, [props.siteId]);

  const handleChange = () => {
    setExpanded(!expanded);
  };

  const handleAdminSelected = async (admin: UserDetailsDTO) => {
    setIsLoadingUserDetails(true);
    setSelectedAdmin(admin);
    const data = await dataViewService.findUserOrgs(
      props.siteId,
      admin.id
    );
    if (data?.orgsMap && Object.keys(data.orgsMap).length > 0) {
      const academicYearId = Object.keys(data.orgsMap)[0];
      setOrgs(data.orgsMap[academicYearId]);
    } else if (orgs.length > 0) {
      setOrgs([]);
    }
    setIsLoadingUserDetails(false);
  };

  return (
    <Box sx={styles.accordion_root}>
      {isLoading ? (
        <Box sx={styles.loaderContainer}>
          <CircularProgress disableShrink />
        </Box>
      ) : null}
      {!isLoading && adminsCount ? (
        <Accordion expanded={expanded} onChange={handleChange}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="course-accordion-content"
          >
            <Typography sx={styles.siteAdminsTitle}>Admins</Typography>
            <Typography sx={styles.siteAdminsCount}>
              { `Count: ${adminsCount ? `${adminsCount.rostered}:${adminsCount.provisioned}:${adminsCount.active}` : 0}` }
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid
              container
              size={{
                xs: 12,
                md: 5,
                lg: 5,
                xl: 5
              }}>
              {expanded ? (
                <AdminsTable
                  siteId={props.siteId}
                  onAdminSelected={handleAdminSelected}
                  selectedAdmin={selectedAdmin}
                ></AdminsTable>
              ) : null}
            </Grid>
            <Grid
              container
              size={{
                xs: 12,
                md: 2,
                lg: 2,
                xl: 2
              }}></Grid>
            <Grid
              container
              size={{
                xs: 12,
                md: 5,
                lg: 5,
                xl: 5
              }}>
              {expanded && selectedAdmin ? (
                <UserDetails
                  userDetails={selectedAdmin}
                  isLoading={isLoadingUserDetails}
                  orgs={orgs}
                  isAdmin={true}
                ></UserDetails>
              ) : null}
            </Grid>
          </AccordionDetails>
        </Accordion>
      ) : null}
    </Box>
  );
}
